import { useState, useEffect, useCallback } from 'react';
import { dashboardService } from '../services/dashboardService';
import { DashboardStats, RecentTransaction } from '../types';

interface UseDashboardReturn {
  stats: DashboardStats;
  recentTransactions: RecentTransaction[];
  loading: boolean;
  error: string | null;
  refreshData: () => Promise<void>;
  lastUpdated: Date | null;
}

export const useDashboard = (autoRefresh = true, refreshInterval = 30000): UseDashboardReturn => {
  const [stats, setStats] = useState<DashboardStats>({
    totalSales: 0,
    totalPurchaseOrders: 0,
    totalProducts: 0,
    totalRevenue: 0,
    salesGrowth: 0,
    revenueGrowth: 0,
    poCount: 0,
  });
  
  const [recentTransactions, setRecentTransactions] = useState<RecentTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const refreshData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      // Don’t block the dashboard on /health. Stats/POS calls wake the API themselves;
      // awaiting wakeBackend() could add many seconds on cold Render tiers.
      void dashboardService.wakeBackend();
      const data = await dashboardService.getDashboardStats();
      
      if (data.stats) {
        setStats(data.stats);
      }
      
      if (data.recentTransactions) {
        setRecentTransactions(data.recentTransactions);
      }
      
      setLastUpdated(new Date());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load dashboard data');
      console.error('Dashboard data fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial data load
  useEffect(() => {
    refreshData();
  }, [refreshData]);

  // Auto refresh setup
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      refreshData();
    }, refreshInterval);

    return () => clearInterval(interval);
  }, [autoRefresh, refreshInterval, refreshData]);

  return {
    stats,
    recentTransactions,
    loading,
    error,
    refreshData,
    lastUpdated,
  };
};